import { Box, Button, FormControl, FormLabel, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notify } from '~/utils/helper';
import styles from './ChangePassword.module.scss';
import { ArrowLeft2Icon, HidePassword, UserIcon, VectorIcon } from '~/assets/icons';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import BaseRow from '~/components/common/base-row/BaseRow';
import { IChangePassword } from '~/utils/interface/user';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { useAppDispatch } from '~/redux/hooks';
import { handleChangePassword } from '~/thunks/auth/authThunk';
import authValidation from '~/validation/auth';

const cx = classNames.bind(styles);

const initialValues: IChangePassword = {
  current_password: '',
  new_password: '',
  confirm_password: '',
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/profile');

  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    enableReinitialize: true,
    validationSchema: authValidation(t).changePassword,
    onSubmit: async (values) => {
      try {
        await dispatch(handleChangePassword(values)).unwrap();
        navigate(`${adminRouteAbsolute.login}`);
      } catch (e: any) {
        notify('error', e || 'Something went wrong.');
      }
    },
  });

  return (
    <Box className='h-screen'>
      <Box className='flex bg-[#123B6D] p-5 w-full mb-3 items-center'>
        <ArrowLeft2Icon className='w-4 mx-5 hover:cursor-pointer' onClick={() => navigate('/admin/dashboard')} />
        <Typography className='text-white text-xl font-semibold'>{t('changePassword.title')}</Typography>
      </Box>
      <Box className='flex justify-center min-h-[352px]'>
        <Box className='flex justify-between bg-white rounded-[12px] w-[540px]'>
          <form className='p-3 w-full' onSubmit={form.handleSubmit}>
            <Box className={cx('input-custom')}>
              <Box className='my-3'>
                <FormControl fullWidth>
                  <FormLabel htmlFor='password' className='mb-2 font-semibold text-[#161A1F] text-[14px]'>
                    {t('changePassword.current_password')}
                  </FormLabel>
                  <TextField
                    name='current_password'
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder={t('changePassword.current_password')}
                    value={form.values.current_password}
                    onChange={form.handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <UserIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label={showCurrentPassword ? 'hide password' : 'show password'}
                            onClick={() => {
                              setShowCurrentPassword(!showCurrentPassword);
                            }}
                            edge='end'
                          >
                            {showCurrentPassword ? <VectorIcon /> : <HidePassword />}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        '& input::-ms-reveal, & input::-ms-clear': {
                          display: 'none',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                        },
                      },
                    }}
                    className='w-full'
                    error={Boolean(form.errors?.current_password)}
                    helperText={form.errors?.current_password}
                    FormHelperTextProps={{
                      sx: {
                        fontSize: '0.75rem',
                        lineHeight: '1rem',
                        margin: '2px 0 0',
                        minHeight: '1rem',
                      },
                    }}
                  />
                </FormControl>
              </Box>
            </Box>
            <Box className={cx('input-custom')}>
              <Box className='my-3'>
                <FormControl fullWidth>
                  <FormLabel htmlFor='password' className='mb-2 font-semibold text-[#161A1F] text-[14px]'>
                    {t('changePassword.new_password')}
                  </FormLabel>
                  <TextField
                    name='new_password'
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder={t('changePassword.new_password')}
                    value={form.values.new_password}
                    onChange={form.handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <UserIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label={showNewPassword ? 'hide password' : 'show password'}
                            onClick={() => {
                              setShowNewPassword(!showNewPassword);
                            }}
                            edge='end'
                          >
                            {showNewPassword ? <VectorIcon /> : <HidePassword />}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        '& input::-ms-reveal, & input::-ms-clear': {
                          display: 'none',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                        },
                      },
                    }}
                    className='w-full'
                    error={Boolean(form.errors?.new_password)}
                    helperText={form.errors?.new_password}
                    FormHelperTextProps={{
                      sx: {
                        fontSize: '0.75rem',
                        lineHeight: '1rem',
                        margin: '2px 0 0',
                        minHeight: '1rem',
                      },
                    }}
                  />
                </FormControl>
              </Box>
            </Box>
            <Box className={cx('input-custom')}>
              <Box className='my-3'>
                <FormControl fullWidth>
                  <FormLabel htmlFor='password' className='mb-2 font-semibold text-[#161A1F] text-[14px]'>
                    {t('changePassword.confirm_password')}
                  </FormLabel>
                  <TextField
                    name='confirm_password'
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={t('changePassword.confirm_password')}
                    value={form.values.confirm_password}
                    onChange={form.handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <UserIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label={showConfirmPassword ? 'hide password' : 'show password'}
                            onClick={() => {
                              setShowConfirmPassword(!showConfirmPassword);
                            }}
                            edge='end'
                          >
                            {showConfirmPassword ? <VectorIcon /> : <HidePassword />}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        '& input::-ms-reveal, & input::-ms-clear': {
                          display: 'none',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '14px',
                        },
                      },
                    }}
                    className='w-full'
                    error={Boolean(form.errors?.confirm_password)}
                    helperText={form.errors?.confirm_password}
                    FormHelperTextProps={{
                      sx: {
                        fontSize: '0.75rem',
                        lineHeight: '1rem',
                        margin: '2px 0 0',
                        minHeight: '1rem',
                      },
                    }}
                  />
                </FormControl>
              </Box>
            </Box>
            <BaseRow className='!justify-between'>
              <Button
                variant='outlined'
                className='min-w-[244px] bg-[#D7DEE7] border-0 !border-inherit text-[#404854] text-base font-semibold'
                onClick={() => navigate('/admin/dashboard')}
              >
                {t('cancel')}
              </Button>
              <Button type='submit' variant='contained' className='min-w-[244px] bg-[#123B6D] text-base font-semibold'>
                {t('update')}
              </Button>
            </BaseRow>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(ChangePassword);
